import React, {useState, useEffect, useRef, useLayoutEffect} from 'react';
import PropTypes from 'prop-types';
import {format, parseISO} from 'date-fns';
import {isLaunchWaitlistDay} from 'packages/common/utils/restaurant/preOpening';

// @mui
import {styled, alpha, useTheme} from '@mui/material/styles';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Fade from '@mui/material/Fade';
import Skeleton from '@mui/material/Skeleton';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';


// components
import {TimeSlotPicker} from '../components/TimeSlotPicker';
import ServiceNote from '../components/ServiceNote';
import NoticeClosedAlert from '../components/NoticeClosedAlert';

// API
import {useReservationData} from 'packages/common/components/table-reservation/context/ReservationDataContext';

// utils
import {useLocalizedText} from 'packages/common/utils/multiLanguageHelper';

// i18n
import {useLocales} from 'packages/common/locales';

// constants
import {ERROR_MESSAGES} from '../constants/reservation';

/**
 * @file Étape de sélection du créneau horaire du flow de réservation.
 * @module packages.common.components.table-reservation.sections.TimeStep
 *
 * Charge de façon asynchrone les créneaux disponibles pour la date, le service et
 * la taille de groupe sélectionnés, puis les présente via TimeSlotPicker. Limite
 * les tentatives de chargement à deux par date et mémorise la dernière date traitée
 * pour éviter les requêtes redondantes. Distingue les créneaux réservables des
 * créneaux uniquement en liste d'attente (`waitlistAvailable && !available`) et
 * propage les informations de waitlist au formulaire parent à la sélection.
 */

// ----------------------------------------------------------------------

const TimeSlotSkeletonLoader = styled(Box)(({theme}) => ({
  width: '100%',
  padding: theme.spacing(1),
}));

const TimeSlotContainer = styled(Box)(({theme}) => ({
  opacity: 1,
  transition: 'opacity 0.4s ease-in-out',
}));

// ----------------------------------------------------------------------

/**
 * Étape de sélection du créneau horaire de réservation.
 *
 * Charge les créneaux disponibles pour la date, le service et la taille de groupe,
 * gère le marquage liste d'attente et enregistre la fonction de validation auprès du parent.
 *
 * @param {Object} props
 * @param {Object} props.formData - Données du formulaire (date, service, partySize, timeSlot)
 * @param {Function} props.updateFormData - Met à jour un champ du formulaire
 * @param {Object} [props.config] - Configuration du restaurant
 * @param {string} [props.companyId] - Identifiant de l'établissement
 * @param {Function} props.setValidation - Enregistre la fonction de validation auprès du parent
 * @param {Function} props.setErrors - Définit les erreurs de validation
 * @param {Object} [props.errors] - Erreurs de validation courantes
 * @param {Function} [props.setStepReady] - Active/désactive le bouton suivant selon la sélection
 * @param {Function} [props.t] - Fonction de traduction générique (fallback sur ERROR_MESSAGES)
 * @param {Function} [props.getCustomText] - Fonction de traduction des textes personnalisés
 * @return {JSX.Element}
 */
export function TimeStep({
  formData,
  updateFormData,
  config,
  companyId,
  setValidation,
  setErrors,
  errors,
  setStepReady,
  t,
  getCustomText,
}) {
  const theme = useTheme();

  const [timeSlots, setTimeSlots] = useState([]);
  const [serviceNoteRaw, setServiceNoteRaw] = useState(null);
  // Horizon ancré sur l'heure du service : le service choisi n'est pas encore
  // ouvert à la réservation. Porte la date et l'heure exactes d'ouverture
  // (`{date: 'YYYY-MM-DD', time: 'HH:mm'}`) renvoyées par l'endpoint des créneaux.
  const [horizonOpening, setHorizonOpening] = useState(null);
  // Date fermée en ligne par le préavis minimum (flag backend) : filet quand le
  // seuil est franchi pendant que le client est sur cette étape.
  const [noticeClosed, setNoticeClosed] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [fetchAttempts, setFetchAttempts] = useState({});
  const [hasDataLoaded, setHasDataLoaded] = useState(false);

  // Garde contre les requêtes concurrentes et mémo de la dernière date chargée
  const fetchingRef = useRef(false);
  const dateRef = useRef(null);
  const timeSlotPickerRef = useRef(null);

  const {getTimeSlots} = useReservationData();
  const {currentLang} = useLocales();
  const dateFnsLocale = currentLang?.adapterLocale;

  // Note d'information du service effectif pour la date choisie, renvoyée par
  // l'endpoint des créneaux (qui résout la cascade exception > planning) et
  // résolue ici dans la langue courante. Affichée au-dessus des créneaux.
  const getLocalizedText = useLocalizedText();
  const serviceNote = getLocalizedText(serviceNoteRaw);

  // Le service est scindé en rotations (« 1er service », « 2e service »...) si les
  // créneaux portent un libellé de rotation. On active alors les en-têtes de groupe.
  const hasServiceRotations = timeSlots.some((slot) => slot && slot.rotationLabel);
  const selectedRotationLabel = formData.selectedTimeSlotData?.rotationLabel;
  const selectedRotationReleaseBy = formData.selectedTimeSlotData?.occupiedUntil;
  // En liste d'attente, le créneau choisi n'est pas une réservation : on masque le
  // bandeau « Vous réservez au... » pour ne pas laisser croire au client qu'il a réservé.
  const selectedIsWaitlist = Boolean(
      formData.selectedTimeSlotData?.waitlistAvailable && !formData.selectedTimeSlotData?.available,
  );

  // Déplace le focus sur le TimeSlotPicker une fois les créneaux affichés (accessibilité clavier)
  useLayoutEffect(() => {
    if (hasDataLoaded && !isInitialLoading && timeSlots.length > 0 && timeSlotPickerRef.current) {
      // Délai pour laisser le DOM se stabiliser après l'animation Fade avant de déplacer le focus
      const timer = setTimeout(() => {
        timeSlotPickerRef.current.focus?.();
      }, 450);
      return () => clearTimeout(timer);
    }
  }, [hasDataLoaded, isInitialLoading, timeSlots.length]);

  const validateStep = React.useCallback(() => {
    if (!formData.timeSlot) {
      setErrors({timeSlot: ERROR_MESSAGES.TIME_REQUIRED});
      return false;
    }
    return true;
  }, [formData.timeSlot, setErrors]);

  useEffect(() => {
    setValidation(validateStep);
  }, [setValidation, formData.timeSlot, validateStep]);

  useEffect(() => {
    if (setStepReady) {
      setStepReady(!!formData.timeSlot);
    }
  }, [formData.timeSlot, setStepReady]);

  // Charge les créneaux dès que la date, le service ou la taille de groupe change
  useEffect(() => {
    if (!companyId || !formData.date || !formData.service || !formData.partySize) {
      return;
    }

    const dateString = format(formData.date, 'yyyy-MM-dd');

    // Choix de salle : les créneaux dépendent de la salle, donc la clé de cache
    // (et le compteur de tentatives) inclut l'`areaId` pour re-fetcher au changement
    // de salle même à date égale.
    const areaId = formData.selectedAreaId || null;
    const fetchKey = areaId ? `${dateString}__${areaId}` : dateString;

    // Abandon après 2 tentatives échouées pour la même date/salle
    if (fetchAttempts[fetchKey] && fetchAttempts[fetchKey] >= 2) {
      setErrors({api: 'Impossible de charger les créneaux. Veuillez réessayer plus tard.'});
      return;
    }

    // Date/salle déjà chargée : pas de nouvelle requête
    if (dateRef.current === fetchKey && hasDataLoaded) {
      return;
    }

    // Requête déjà en vol : on ne la double pas
    if (fetchingRef.current) {
      return;
    }

    fetchingRef.current = true;
    setIsInitialLoading(true);
    setHasDataLoaded(false);

    if (errors.timeSlot || errors.api) {
      setErrors({});
    }

    dateRef.current = fetchKey;

    setFetchAttempts((prev) => ({
      ...prev,
      [fetchKey]: (prev[fetchKey] || 0) + 1,
    }));

    const fetchTimeSlots = async () => {
      try {
        const params = {
          date: dateString,
          service: formData.service,
          partySize: formData.partySize,
          // Restreint les créneaux à la salle choisie (omis si pas de choix de salle).
          ...(areaId ? {areaId} : {}),
        };

        const response = await getTimeSlots(params);

        // La réponse peut être un tableau brut ou un objet { timeslots, serviceNote?, noticeClosed? }
        let extractedTimeSlots = [];
        let note = null;
        if (response) {
          if (response.timeslots && Array.isArray(response.timeslots)) {
            extractedTimeSlots = response.timeslots;
            note = response.serviceNote || null;
          } else if (Array.isArray(response)) {
            extractedTimeSlots = response;
          }
          setTimeSlots(extractedTimeSlots);
        } else {
          setTimeSlots([]);
        }
        setServiceNoteRaw(note);
        // Service pas encore ouvert (horizon ancré sur l'heure du service) :
        // aucun créneau n'est renvoyé, on affiche la date et l'heure d'ouverture.
        setHorizonOpening(response?.horizonNotYetOpen && response.opensAtDate ?
          {date: response.opensAtDate, time: response.opensAtTime} :
          null);
        setNoticeClosed(Boolean(response?.noticeClosed));

        setHasDataLoaded(true);
      } catch (apiError) {
        setErrors({
          api: apiError.response?.data?.message || ERROR_MESSAGES.GENERAL_ERROR,
        });
        setTimeSlots([]);
        setServiceNoteRaw(null);
        setHorizonOpening(null);
        setNoticeClosed(false);
        setHasDataLoaded(true);
      } finally {
        setIsInitialLoading(false);
        fetchingRef.current = false;
      }
    };

    fetchTimeSlots();
  }, [companyId, formData.date, formData.service, formData.partySize, formData.selectedAreaId, fetchAttempts, getTimeSlots, errors.api, errors.timeSlot, hasDataLoaded, setErrors]);


  const handleTimeSelect = (time, slotData) => {
    updateFormData('timeSlot', time);

    if (slotData) {
      // Données complètes du créneau conservées pour les services à cheval sur minuit
      updateFormData('selectedTimeSlotData', slotData);

      // Créneau complet mais ouvert en liste d'attente : on bascule la réservation en waitlist
      const isWaitlistSlot = slotData.waitlistAvailable && !slotData.available;
      updateFormData('isWaitlistReservation', isWaitlistSlot);
      if (isWaitlistSlot) {
        updateFormData('waitlistInfo', {
          remaining: slotData.waitlistRemaining,
        });
      }
    }

    if (errors.timeSlot) {
      setErrors({});
    }
  };

  const renderTimeSlotSkeleton = () => (
    <TimeSlotSkeletonLoader>
      <Grid container spacing={1.5}>
        {[...Array(3)].map((_, rowIndex) => (
          <Grid container item spacing={1} key={`row-${rowIndex}`}>
            {[...Array(4)].map((_, colIndex) => (
              <Grid item xs={3} key={`slot-${rowIndex}-${colIndex}`}>
                <Skeleton
                  variant="rounded"
                  width="100%"
                  height={36}
                  animation="wave"
                />
              </Grid>
            ))}
          </Grid>
        ))}
      </Grid>
    </TimeSlotSkeletonLoader>
  );

  return (
    <Fade in timeout={300}>
      <Box>
        <Typography variant="h6" sx={{fontWeight: 600, mb: 3, textAlign: 'center'}}>
          {t ? t('widgets.reservation.form.timeQuestion') : 'Quel horaire souhaitez-vous réserver ?'}
        </Typography>

        {serviceNote && (
          <Fade in timeout={400}>
            <Box>
              <ServiceNote note={serviceNote} />
            </Box>
          </Fade>
        )}

        {isInitialLoading ? (
          renderTimeSlotSkeleton()
        ) : !hasDataLoaded ? (
          renderTimeSlotSkeleton()
        ) : timeSlots.length === 0 ? (
          <Fade in timeout={400}>
            <Box>
              {horizonOpening ? (
                // Service pas encore ouvert (horizon ancré sur l'heure du service).
                <Alert severity="info" sx={{mb: 2, py: 0.5}}>
                  {t ?
                    t('widgets.reservation.form.serviceNotYetOpen', {
                      date: format(parseISO(horizonOpening.date), 'EEEE d MMMM', dateFnsLocale ? {locale: dateFnsLocale} : undefined),
                      time: horizonOpening.time,
                    }) :
                    `Les réservations en ligne pour ce service ouvriront le ${format(parseISO(horizonOpening.date), 'EEEE d MMMM', dateFnsLocale ? {locale: dateFnsLocale} : undefined)} à ${horizonOpening.time}`}
                </Alert>
              ) : noticeClosed && !errors.api ? (
                // Fermé en ligne par le préavis minimum : invitation à réserver par téléphone.
                <NoticeClosedAlert
                  getCustomText={getCustomText}
                  t={t}
                  phone={config?.contactPhone || null}
                  sx={{mb: 2, py: 0.5}}
                />
              ) : (
                <Alert severity="info" sx={{mb: 2, py: 0.5}}>
                  {errors.api || ERROR_MESSAGES.NO_AVAILABILITY}
                </Alert>
              )}
            </Box>
          </Fade>
        ) : timeSlots.every((slot) => slot.waitlistAvailable && !slot.available) ? (
          <>
            <Fade in timeout={400}>
              <Alert
                severity="warning"
                sx={{
                  'mb': 2,
                  'py': 0.5,
                  'borderColor': theme.palette.warning.main,
                  'backgroundColor': alpha(theme.palette.warning.main, 0.08),
                  'justifyContent': 'center',
                  '& .MuiAlert-icon': {color: theme.palette.warning.dark},
                  '& .MuiAlert-message': {color: theme.palette.warning.dark, textAlign: 'center'},
                }}
              >
                {/* Liste d'attente de lancement : le service n'est pas complet, les réservations ne sont pas encore ouvertes. */}
                {isLaunchWaitlistDay(config?.preOpening, formData.date) ?
                  t('widgets.reservation.preOpening.launchWaitlistHint') :
                  t('widgets.reservation.form.serviceFullWaitlist')}
              </Alert>
            </Fade>
            <Fade in timeout={400}>
              <TimeSlotContainer>
                <TimeSlotPicker
                  ref={timeSlotPickerRef}
                  timeSlots={timeSlots}
                  selectedTime={formData.timeSlot}
                  onTimeSelect={handleTimeSelect}
                  compact={true}
                  showGroupHeaders={hasServiceRotations}
                />
              </TimeSlotContainer>
            </Fade>
          </>
        ) : (
          <Fade in timeout={400}>
            <TimeSlotContainer>
              <TimeSlotPicker
                ref={timeSlotPickerRef}
                timeSlots={timeSlots}
                selectedTime={formData.timeSlot}
                onTimeSelect={handleTimeSelect}
                compact={true}
                showGroupHeaders={hasServiceRotations}
              />
            </TimeSlotContainer>
          </Fade>
        )}

        {formData.timeSlot && selectedRotationLabel && !selectedIsWaitlist && (
          <Fade in timeout={300}>
            <Alert
              severity="info"
              icon={false}
              sx={{
                'mt': 2,
                'mb': 3,
                'py': 0.75,
                'borderColor': theme.palette.primary.main,
                'backgroundColor': alpha(theme.palette.primary.main, 0.08),
                '& .MuiAlert-message': {color: theme.palette.primary.darker, textAlign: 'center', width: '100%'},
              }}
            >
              Vous réservez au <strong>{selectedRotationLabel}</strong>
              {selectedRotationReleaseBy && (
                <>
                  , la table devra être libérée pour <strong>{selectedRotationReleaseBy}</strong>
                </>
              )}
              .
            </Alert>
          </Fade>
        )}

        {errors.timeSlot && timeSlots.length > 0 && (
          <Alert severity="error" sx={{mt: 2, mb: 2, py: 0.5}}>
            {errors.timeSlot}
          </Alert>
        )}
      </Box>
    </Fade>
  );
}

TimeStep.propTypes = {
  formData: PropTypes.object.isRequired,
  updateFormData: PropTypes.func.isRequired,
  config: PropTypes.object,
  companyId: PropTypes.string,
  setValidation: PropTypes.func.isRequired,
  setErrors: PropTypes.func.isRequired,
  errors: PropTypes.object,
  setStepReady: PropTypes.func,
  t: PropTypes.func,
  getCustomText: PropTypes.func,
};
